// V2 Settings — connected accounts · sending rules · calendar · legacy.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  PasswordInput,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCamera,
  IconBrandLinkedin,
  IconBrandWechat,
  IconBrandWhatsapp,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconDeviceMobile,
  IconExternalLink,
  IconMail,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import {
  acceptSendConsent,
  fetchAgentTokens,
  fetchMyPrincipal,
  fetchLatestExtension,
  generateAgentPairingCode,
  fetchProspectAutoStatus,
  revokeAgentToken,
  startUnipileLinkFlow,
  triggerWebsiteCrawlNow,
  triggerOrganicBackfill,
  unlinkUnipileAccount,
  uploadPrincipalPhoto,
  updateMyPrincipal,
} from '../services/api';
import OrganicSyncBanner from '../v2/OrganicSyncBanner';

const DEBOUNCE_MS = 400;

// v3.3.1: static IANA list backing the Select in place of the free-text
// TextInput. Prior TextInput bug: per-keystroke debounce sent partial
// values (A / As / Asi…), normalizeTimezone() rejected them all to
// Australia/Sydney, coach could never finish typing. Select fixes it
// structurally — no partials possible.
const TIMEZONE_OPTIONS = [
  { group: 'Australia / NZ', items: [
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
    'Australia/Adelaide', 'Australia/Perth', 'Australia/Hobart',
    'Pacific/Auckland',
  ]},
  { group: 'Asia', items: [
    'Asia/Colombo', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo',
    'Asia/Shanghai', 'Asia/Bangkok', 'Asia/Kuala_Lumpur', 'Asia/Manila',
    'Asia/Jakarta', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Seoul',
  ]},
  { group: 'Europe', items: [
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam',
    'Europe/Zurich', 'Europe/Madrid', 'Europe/Rome', 'Europe/Stockholm',
    'Europe/Dublin',
  ]},
  { group: 'Americas', items: [
    'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Toronto', 'America/Vancouver',
    'America/Sao_Paulo', 'America/Mexico_City', 'America/Argentina/Buenos_Aires',
  ]},
  { group: 'Africa / Middle East', items: [
    'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Cairo',
    'Africa/Nairobi', 'Asia/Riyadh', 'Asia/Jerusalem',
  ]},
].map((g) => ({ group: g.group, items: g.items.map((tz) => ({ value: tz, label: tz })) }));

// Design defaults — match the ones in api/src/models/principal.js.
const DEFAULTS = {
  businessHoursOnly: true,
  dailySendCap: 25,
  followUpsCount: 2,
  followUpSpacingDays: 4,
  recoolDays: 5,
  approvalRequired: true,
  bookingUrl: '',
  dailyInviteCap: 20,
  timezone: 'Australia/Sydney',
  websiteUrl: '',
  crawlIncludePatterns: [],
  crawlExcludePatterns: [],
  crawlMaxPages: 100,
};

const CHANNEL_MODE_DEFAULTS = {
  email: 0,
  linkedin: 0,
  whatsapp: 0,
  sms: 0,
  wechat: 0,
};

const BYLINE_DEFAULTS = {
  displayName: '',
  photoUrl: '',
  shortBio: '',
  credentialLine: '',
  ctaLabel: 'Book a 1:1 session',
  ctaUrl: '',
};

const CONSENT_COPY = `By enabling auto-send and linking your account, you are authorising The Aviary to send outreach on your behalf through Unipile.

Important risk notice:
- LinkedIn, WhatsApp, and Instagram automation can violate platform terms and may lead to account restrictions.
- Email integrations (Google/Microsoft/IMAP) are lower risk, but outbound activity is still your responsibility.
- The Aviary stores account mappings and message metadata, and uses third-party infrastructure to deliver messages.

I understand these risks and accept responsibility for sends initiated from my connected accounts.`;

const SEND_MODE_CHANNELS = [
  { key: 'email', label: 'Email', Icon: IconMail },
  { key: 'linkedin', label: 'LinkedIn', Icon: IconBrandLinkedin },
  { key: 'whatsapp', label: 'WhatsApp', Icon: IconBrandWhatsapp },
  { key: 'sms', label: 'SMS', Icon: IconDeviceMobile },
  { key: 'wechat', label: 'WeChat', Icon: IconBrandWechat },
];

const MODE_LABELS = { 0: 'Manual', 1: 'Confirm', 2: 'Auto' };

function SectionLabel({ children }) {
  return (
    <Text
      size="xs"
      fw={700}
      style={{ color: 'var(--aviary-muted)', letterSpacing: 1.2, textTransform: 'uppercase' }}
    >
      {children}
    </Text>
  );
}

function Row({ label, control, sub }) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" style={{ padding: '14px 0' }}>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        {typeof label === 'string'
          ? <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>{label}</Text>
          : label}
        {sub && <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{sub}</Text>}
      </Stack>
      {control}
    </Group>
  );
}

function normalizedChannelModes(raw) {
  const merged = { ...CHANNEL_MODE_DEFAULTS };
  if (!raw || typeof raw !== 'object') return merged;
  for (const [channel, fallback] of Object.entries(CHANNEL_MODE_DEFAULTS)) {
    const n = Number(raw[channel]);
    merged[channel] = [0, 1, 2].includes(n) ? n : fallback;
  }
  return merged;
}

function helperTintForMode(mode) {
  if (mode === 0) return 'var(--mantine-color-sage-7)';
  if (mode === 2) return 'var(--mantine-color-green-7)';
  return 'var(--aviary-muted)';
}

function seenAgo(iso) {
  const ts = Date.parse(iso || '');
  if (Number.isNaN(ts)) return 'just now';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

function formatDateTime(iso) {
  const ts = Date.parse(String(iso || ''));
  if (Number.isNaN(ts)) return 'Never';
  return new Date(ts).toLocaleString();
}

function isValidHttpsUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'https:';
  } catch (_err) {
    return false;
  }
}

export default function AviaryV2SettingsPage() {
  const api = useApi();
  const [principal, setPrincipal] = useState(null);
  const [error, setError] = useState(null);
  const [bylineDraft, setBylineDraft] = useState(BYLINE_DEFAULTS);
  const [bylineSaving, setBylineSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [linkingLinkedin, setLinkingLinkedin] = useState(false);
  const [unlinkingLinkedin, setUnlinkingLinkedin] = useState(false);
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [consentSaving, setConsentSaving] = useState(false);
  const [autoRiskModalOpen, setAutoRiskModalOpen] = useState(false);
  const [enablingAuto, setEnablingAuto] = useState(false);
  const [prospectAutoStatus, setProspectAutoStatus] = useState(null);
  const [extensionRelease, setExtensionRelease] = useState(null);
  const [extensionReleaseError, setExtensionReleaseError] = useState(null);
  const [extensionInstallOpen, setExtensionInstallOpen] = useState(false);
  const [organicSyncError, setOrganicSyncError] = useState('');
  const [organicSyncRunning, setOrganicSyncRunning] = useState(false);
  const [sendMethodError, setSendMethodError] = useState('');
  const [twilioDraft, setTwilioDraft] = useState({
    accountSid: '',
    authToken: '',
    fromNumber: '',
    messagingServiceSid: '',
  });
  const [twilioEditing, setTwilioEditing] = useState(false);
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [twilioFieldError, setTwilioFieldError] = useState('');
  const [twilioClearModalOpen, setTwilioClearModalOpen] = useState(false);
  const [twilioClearing, setTwilioClearing] = useState(false);
  const [agentTokens, setAgentTokens] = useState([]);
  const [agentTokensLoading, setAgentTokensLoading] = useState(false);
  const [agentPairingLoading, setAgentPairingLoading] = useState(false);
  const [agentPairingCode, setAgentPairingCode] = useState('');
  const [agentPairingExpiresAt, setAgentPairingExpiresAt] = useState(null);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState(null);
  const [tokenRevoking, setTokenRevoking] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const [crawlNowRunning, setCrawlNowRunning] = useState(false);
  const savedRef = useRef({});
  const timerRef = useRef({});
  const photoInputRef = useRef(null);

  const loadPrincipal = useCallback(async () => {
    const [p, status] = await Promise.all([
      fetchMyPrincipal(api),
      fetchProspectAutoStatus(api).catch(() => null),
    ]);
    const merged = {
      ...DEFAULTS,
      ...BYLINE_DEFAULTS,
      ...p,
      channelModes: normalizedChannelModes(p?.channelModes),
    };
    // v3.3.1: auto-detect timezone on first load if the principal doesn't have
    // one set. Uses browser's IANA timezone via Intl. Silently PATCHes and
    // updates local state so the coach doesn't hit "Outside business hours"
    // just because nobody prompted them for their timezone. Only fires if the
    // browser's timezone is in TIMEZONE_OPTIONS — refuses to save an unlisted
    // tz that the coach can't later change via the dropdown.
    if (!p?.timezone) {
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const inList = TIMEZONE_OPTIONS.some((g) => g.items.some((i) => i.value === browserTz));
        if (browserTz && inList) {
          merged.timezone = browserTz;
          updateMyPrincipal(api, { timezone: browserTz })
            .then(() => console.info(`[settings] auto-detected timezone: ${browserTz}`))
            .catch((err) => console.warn('[settings] timezone auto-detect PATCH failed', err?.message));
        }
      } catch (_err) { /* browser without Intl support — extremely rare, skip */ }
    }

    setPrincipal(merged);
    setOrganicSyncError('');
    setBylineDraft({
      displayName: String(merged.displayName || ''),
      photoUrl: String(merged.photoUrl || ''),
      shortBio: String(merged.shortBio || ''),
      credentialLine: String(merged.credentialLine || ''),
      ctaLabel: String(merged.ctaLabel || BYLINE_DEFAULTS.ctaLabel),
      ctaUrl: String(merged.ctaUrl || ''),
    });
    savedRef.current = { ...merged };
    setProspectAutoStatus(status);
    setTwilioDraft({
      accountSid: '',
      authToken: '',
      fromNumber: '',
      messagingServiceSid: '',
    });
    setTwilioFieldError('');
    setTwilioEditing(!(merged?.twilio?.configured === true));
  }, [api]);

  const refreshStatus = useCallback(async () => {
    const status = await fetchProspectAutoStatus(api).catch(() => null);
    setProspectAutoStatus(status);
  }, [api]);

  const loadAgentAccess = useCallback(async () => {
    setAgentTokensLoading(true);
    try {
      const tokens = await fetchAgentTokens(api);
      setAgentTokens(Array.isArray(tokens) ? tokens : []);
    } catch (err) {
      notifications.show({
        title: 'Could not load agent tokens',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setAgentTokensLoading(false);
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    loadPrincipal()
      .then(() => { if (!cancelled) setError(null); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); });
    return () => { cancelled = true; };
  }, [loadPrincipal]);

  useEffect(() => {
    let cancelled = false;
    fetchLatestExtension(api)
      .then((payload) => {
        if (cancelled) return;
        setExtensionRelease(payload || null);
        setExtensionReleaseError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (Number(err?.response?.status) === 404) {
          setExtensionRelease(null);
          setExtensionReleaseError(null);
          return;
        }
        setExtensionRelease(null);
        setExtensionReleaseError(err?.response?.data?.error || err.message || 'Could not load extension release');
      });
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => {
    loadAgentAccess().catch(() => {});
  }, [loadAgentAccess]);

  useEffect(() => {
    if (!principal?.organicSyncState?.syncing) return undefined;
    const timer = setInterval(() => {
      loadPrincipal().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [principal?.organicSyncState?.syncing, loadPrincipal]);

  const saveField = useCallback(async (key, value) => {
    setSavingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    try {
      const patch = { [key]: value };
      const updated = await updateMyPrincipal(api, patch);
      savedRef.current[key] = value;
      // Reflect server-normalised value (e.g. Math.round on numbers) back into state.
      if (updated && typeof updated === 'object' && key in updated) {
        setPrincipal((prev) => (prev ? { ...prev, [key]: updated[key] } : prev));
      }
    } catch (err) {
      notifications.show({
        title: 'Could not save',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
      // Revert on error.
      setPrincipal((prev) => (prev ? { ...prev, [key]: savedRef.current[key] } : prev));
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [api]);

  const patchField = useCallback((key, value) => {
    setPrincipal((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (timerRef.current[key]) clearTimeout(timerRef.current[key]);
    timerRef.current[key] = setTimeout(() => {
      if (savedRef.current[key] !== value) saveField(key, value);
    }, DEBOUNCE_MS);
  }, [saveField]);

  const patchWebsiteUrl = useCallback((value) => {
    const next = String(value || '').trim();
    setPrincipal((prev) => (prev ? { ...prev, websiteUrl: next } : prev));
    if (!isValidHttpsUrl(next)) {
      setWebsiteError('Website URL must be a valid https URL.');
      return;
    }
    setWebsiteError('');
    patchField('websiteUrl', next);
  }, [patchField]);

  const patchPatternField = useCallback((key, raw) => {
    const list = String(raw || '')
      .split('\n')
      .map((row) => row.trim())
      .filter(Boolean);
    patchField(key, list);
  }, [patchField]);

  const patchCrawlMaxPages = useCallback((value) => {
    const n = Number(value);
    const clamped = Number.isFinite(n) ? Math.min(500, Math.max(1, Math.round(n))) : 100;
    patchField('crawlMaxPages', clamped);
  }, [patchField]);

  const setChannelMode = useCallback(async (channel, mode) => {
    const modeNum = Number(mode);
    if (![0, 1, 2].includes(modeNum)) return;
    const key = `channelModes:${channel}`;
    const previous = normalizedChannelModes(principal?.channelModes);
    const next = { ...previous, [channel]: modeNum };

    setPrincipal((prev) => (prev ? { ...prev, channelModes: next } : prev));
    setSavingKeys((prev) => {
      const copy = new Set(prev);
      copy.add(key);
      return copy;
    });
    try {
      const updated = await updateMyPrincipal(api, { channelModes: { [channel]: modeNum } });
      const mergedModes = normalizedChannelModes(updated?.channelModes);
      setPrincipal((prev) => (prev ? { ...prev, channelModes: mergedModes } : prev));
      savedRef.current = { ...savedRef.current, channelModes: mergedModes };
    } catch (err) {
      setPrincipal((prev) => (prev ? { ...prev, channelModes: previous } : prev));
      notifications.show({
        title: 'Could not save mode',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSavingKeys((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    }
  }, [api, principal?.channelModes]);

  async function doAcceptConsent() {
    setConsentSaving(true);
    try {
      await acceptSendConsent(api);
      await loadPrincipal();
      notifications.show({ color: 'sage', title: 'Auto-send enabled', message: 'Consent was recorded.' });
    } catch (err) {
      notifications.show({ color: 'red', title: 'Could not save consent', message: err.response?.data?.error || err.message });
    } finally {
      setConsentSaving(false);
    }
  }

  async function enableProspectAutoWithConsent() {
    setEnablingAuto(true);
    try {
      await updateMyPrincipal(api, {
        inviteAutoConsentAt: true,
      });
      await updateMyPrincipal(api, {
        laneAutomation: { prospect: 'auto' },
      });
      await loadPrincipal();
      notifications.show({
        color: 'sage',
        title: 'Prospect auto enabled',
        message: 'LinkedIn invites will send via Unipile in business hours with jitter.',
      });
      setAutoRiskModalOpen(false);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not enable auto mode',
        message: err.response?.data?.error || err.message,
      });
    } finally {
      setEnablingAuto(false);
    }
  }

  const patchBylineDraft = useCallback((key, value) => {
    setBylineDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveByline = useCallback(async () => {
    setBylineSaving(true);
    try {
      const payload = {
        displayName: String(bylineDraft.displayName || ''),
        photoUrl: String(bylineDraft.photoUrl || ''),
        shortBio: String(bylineDraft.shortBio || ''),
        credentialLine: String(bylineDraft.credentialLine || ''),
        ctaLabel: String(bylineDraft.ctaLabel || ''),
        ctaUrl: String(bylineDraft.ctaUrl || ''),
      };
      const updated = await updateMyPrincipal(api, payload);
      const mergedByline = {
        displayName: String(updated?.displayName || ''),
        photoUrl: String(updated?.photoUrl || ''),
        shortBio: String(updated?.shortBio || ''),
        credentialLine: String(updated?.credentialLine || ''),
        ctaLabel: String(updated?.ctaLabel || BYLINE_DEFAULTS.ctaLabel),
        ctaUrl: String(updated?.ctaUrl || ''),
      };
      setPrincipal((prev) => (prev ? { ...prev, ...mergedByline } : prev));
      setBylineDraft(mergedByline);
      savedRef.current = { ...savedRef.current, ...mergedByline };
      notifications.show({
        color: 'sage',
        title: 'Saved',
        message: 'Article byline updated.',
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not save byline',
        message: err.response?.data?.error || err.message,
      });
    } finally {
      setBylineSaving(false);
    }
  }, [api, bylineDraft]);

  const linkedLinkedinAccount = (Array.isArray(principal?.unipileAccounts) ? principal.unipileAccounts : [])
    .find((entry) => String(entry?.channel || '').toLowerCase() === 'linkedin') || null;

  const connectLinkedin = useCallback(async () => {
    if (linkingLinkedin) return;
    setLinkingLinkedin(true);
    try {
      const { authUrl } = await startUnipileLinkFlow(api, { channel: 'linkedin' });
      if (!authUrl) throw new Error('Missing hosted auth URL');
      window.open(authUrl, '_blank', 'noopener,noreferrer');

      const startedAt = Date.now();
      let linked = false;
      while (!linked && (Date.now() - startedAt) < 60000) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 3000));
        // eslint-disable-next-line no-await-in-loop
        const nextPrincipal = await fetchMyPrincipal(api);
        const merged = {
          ...DEFAULTS,
          ...BYLINE_DEFAULTS,
          ...nextPrincipal,
          channelModes: normalizedChannelModes(nextPrincipal?.channelModes),
        };
        const hasLinkedin = (Array.isArray(merged.unipileAccounts) ? merged.unipileAccounts : [])
          .some((entry) => String(entry?.channel || '').toLowerCase() === 'linkedin');
        if (hasLinkedin) {
          setPrincipal(merged);
          savedRef.current = { ...savedRef.current, ...merged };
          linked = true;
        }
      }
      if (!linked) {
        throw new Error('Timed out waiting for LinkedIn account link');
      }
      notifications.show({
        color: 'sage',
        title: 'LinkedIn connected',
        message: 'Aviary is now listening for invite acceptances.',
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not connect LinkedIn',
        message: err.response?.data?.error || err.message,
      });
    } finally {
      setLinkingLinkedin(false);
    }
  }, [api, linkingLinkedin]);

  const disconnectLinkedin = useCallback(async () => {
    if (!linkedLinkedinAccount || unlinkingLinkedin) return;
    setUnlinkingLinkedin(true);
    try {
      await unlinkUnipileAccount(api, {
        channel: 'linkedin',
        accountId: String(linkedLinkedinAccount.accountId || ''),
      });
      await loadPrincipal();
      notifications.show({
        color: 'sage',
        title: 'LinkedIn disconnected',
        message: 'Inbound acceptance detection is now off.',
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not unlink account',
        message: err.response?.data?.error || err.message,
      });
    } finally {
      setUnlinkingLinkedin(false);
    }
  }, [api, linkedLinkedinAccount, loadPrincipal, unlinkingLinkedin]);

  const handleOrganicSyncModeChange = useCallback(async (nextMode) => {
    if (!principal) return;
    const normalizedNext = String(nextMode || '').trim();
    if (!['match-only', 'import-new'].includes(normalizedNext)) return;
    const previous = String(principal.organicSyncMode || (principal.autoIngestOrganicConnects === true ? 'import-new' : 'match-only'));
    setOrganicSyncError('');
    setPrincipal((prev) => (prev ? { ...prev, organicSyncMode: normalizedNext } : prev));
    try {
      const updated = await updateMyPrincipal(api, { organicSyncMode: normalizedNext });
      const resolvedMode = String(
        updated?.organicSyncMode
        || (updated?.autoIngestOrganicConnects === true ? 'import-new' : 'match-only')
      );
      setPrincipal((prev) => (prev ? {
        ...prev,
        ...updated,
        organicSyncMode: resolvedMode,
        organicSyncState: updated?.organicSyncState || prev.organicSyncState,
      } : prev));
    } catch (err) {
      setPrincipal((prev) => (prev ? { ...prev, organicSyncMode: previous } : prev));
      const code = String(err?.response?.data?.code || '').trim();
      const message = err?.response?.data?.error || err.message || 'Could not update Organic sync.';
      if (code === 'LINKEDIN_NOT_LINKED') {
        setOrganicSyncError(message);
      } else if (code === 'INVALID_ORGANIC_SYNC_MODE') {
        notifications.show({ color: 'red', title: 'Invalid mode', message });
      } else {
        notifications.show({ color: 'red', title: 'Could not save', message });
      }
    }
  }, [api, principal]);

  const handleSendMethodChange = useCallback(async (nextMethod) => {
    if (!principal) return;
    const normalizedNext = String(nextMethod || '').trim();
    if (!['extension', 'unipile', 'sms'].includes(normalizedNext)) return;
    const previous = String(principal.sendMethod || 'extension');
    if (normalizedNext === previous) return;
    setSendMethodError('');
    setPrincipal((prev) => (prev ? { ...prev, sendMethod: normalizedNext } : prev));
    try {
      const updated = await updateMyPrincipal(api, { sendMethod: normalizedNext });
      setPrincipal((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (err) {
      setPrincipal((prev) => (prev ? { ...prev, sendMethod: previous } : prev));
      const code = String(err?.response?.data?.code || '').trim();
      const message = err?.response?.data?.error || err.message || 'Could not update send method.';
      if (code === 'UNIPILE_ACCOUNT_REQUIRED') {
        setSendMethodError(message);
      } else {
        notifications.show({ color: 'red', title: 'Could not save', message });
      }
    }
  }, [api, principal]);

  const patchTwilioDraft = useCallback((key, value) => {
    setTwilioDraft((prev) => ({ ...prev, [key]: String(value || '') }));
    setTwilioFieldError('');
  }, []);

  const saveTwilioConfig = useCallback(async () => {
    const accountSid = String(twilioDraft.accountSid || '').trim();
    const authToken = String(twilioDraft.authToken || '').trim();
    const fromNumber = String(twilioDraft.fromNumber || '').trim();
    const messagingServiceSid = String(twilioDraft.messagingServiceSid || '').trim();
    if (!accountSid || !authToken || (!fromNumber && !messagingServiceSid)) {
      notifications.show({
        color: 'red',
        title: 'Missing fields',
        message: 'Account SID, Auth Token, and either From Number or Messaging Service SID are required.',
      });
      return;
    }
    setTwilioSaving(true);
    setTwilioFieldError('');
    try {
      await updateMyPrincipal(api, {
        twilio: {
          accountSid,
          authToken,
          fromNumber: fromNumber || null,
          messagingServiceSid: messagingServiceSid || null,
        },
      });
      await loadPrincipal();
      setTwilioEditing(false);
      notifications.show({
        color: 'sage',
        title: 'Twilio saved',
        message: 'SMS configuration updated.',
      });
    } catch (err) {
      const code = String(err?.response?.data?.code || '').trim();
      const message = err?.response?.data?.error || err.message || 'Could not save Twilio configuration.';
      if (code === 'INVALID_ACCOUNT_SID') {
        setTwilioFieldError('Account SID must match AC followed by 32 lowercase hex characters.');
      } else if (code === 'INVALID_TWILIO_FROM_NUMBER') {
        setTwilioFieldError('From Number must be Australian E.164 format (for example +61412345678).');
      } else if (code === 'INVALID_TWILIO_MESSAGING_SERVICE_SID') {
        setTwilioFieldError('Messaging Service SID must match MG followed by 32 lowercase hex characters.');
      } else if (code === 'TWILIO_PARTIAL_UPDATE_NOT_ALLOWED') {
        notifications.show({ color: 'red', title: 'Could not save Twilio', message });
      } else {
        notifications.show({ color: 'red', title: 'Could not save Twilio', message });
      }
    } finally {
      setTwilioSaving(false);
    }
  }, [api, loadPrincipal, twilioDraft]);

  const clearTwilioConfig = useCallback(async () => {
    setTwilioClearing(true);
    try {
      await updateMyPrincipal(api, { twilio: null });
      await loadPrincipal();
      setTwilioClearModalOpen(false);
      setTwilioEditing(true);
      notifications.show({
        color: 'sage',
        title: 'Twilio cleared',
        message: 'SMS configuration removed.',
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not clear Twilio',
        message: err?.response?.data?.error || err.message,
      });
    } finally {
      setTwilioClearing(false);
    }
  }, [api, loadPrincipal]);

  const triggerBackfillNow = useCallback(async () => {
    setOrganicSyncRunning(true);
    setOrganicSyncError('');
    try {
      await triggerOrganicBackfill(api);
      await loadPrincipal();
      notifications.show({
        color: 'sage',
        title: 'Backfill started',
        message: 'LinkedIn connection sync is running in the background.',
      });
    } catch (err) {
      const message = err?.response?.data?.error || err.message || 'Could not start backfill.';
      setOrganicSyncError(message);
      notifications.show({ color: 'red', title: 'Could not start backfill', message });
    } finally {
      setOrganicSyncRunning(false);
    }
  }, [api, loadPrincipal]);

  const triggerWebsiteCrawl = useCallback(async () => {
    setCrawlNowRunning(true);
    try {
      const result = await triggerWebsiteCrawlNow(api);
      notifications.show({
        color: 'sage',
        title: 'Website crawl complete',
        message: `Visited ${Number(result?.stats?.pagesVisited || 0)} pages, changed ${Number(result?.stats?.pagesChanged || 0)}.`,
      });
      await loadPrincipal();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Could not run crawl',
        message: err?.response?.data?.error || err.message,
      });
    } finally {
      setCrawlNowRunning(false);
    }
  }, [api, loadPrincipal]);

  const handleGeneratePairingCode = useCallback(async () => {
    setAgentPairingLoading(true);
    try {
      const result = await generateAgentPairingCode(api);
      setAgentPairingCode(String(result?.code || ''));
      setAgentPairingExpiresAt(result?.expiresAt || null);
      notifications.show({
        title: 'Pairing code generated',
        message: 'Code expires in 15 minutes and replaces prior unused codes.',
        color: 'sage',
      });
      await loadAgentAccess();
    } catch (err) {
      notifications.show({
        title: 'Could not generate pairing code',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setAgentPairingLoading(false);
    }
  }, [api, loadAgentAccess]);

  const handleCopyPairingCode = useCallback(async () => {
    if (!agentPairingCode) return;
    try {
      await navigator.clipboard.writeText(agentPairingCode);
      notifications.show({
        title: 'Copied',
        message: 'Pairing code copied to clipboard.',
        color: 'sage',
      });
    } catch (_err) {
      notifications.show({
        title: 'Could not copy',
        message: 'Copy the code manually.',
        color: 'red',
      });
    }
  }, [agentPairingCode]);

  const confirmRevokeToken = useCallback(async () => {
    if (!tokenToRevoke?.tokenId) return;
    setTokenRevoking(true);
    try {
      await revokeAgentToken(api, tokenToRevoke.tokenId);
      notifications.show({
        title: 'Token revoked',
        message: 'Agent token access was revoked.',
        color: 'sage',
      });
      setRevokeModalOpen(false);
      setTokenToRevoke(null);
      await loadAgentAccess();
    } catch (err) {
      notifications.show({
        title: 'Could not revoke token',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setTokenRevoking(false);
    }
  }, [api, loadAgentAccess, tokenToRevoke]);

  const onPhotoPicked = useCallback(async (event) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      notifications.show({ color: 'red', title: 'Invalid file', message: 'Upload a JPEG or PNG image.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notifications.show({ color: 'red', title: 'File too large', message: 'Photo must be 5MB or smaller.' });
      return;
    }
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const result = await uploadPrincipalPhoto(api, formData);
      const nextUrl = String(result?.photoUrl || '');
      setBylineDraft((prev) => ({ ...prev, photoUrl: nextUrl }));
      notifications.show({ color: 'sage', title: 'Photo uploaded', message: 'Click Save to persist it to your profile.' });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Upload failed',
        message: err.response?.data?.error || err.message,
      });
    } finally {
      setPhotoUploading(false);
    }
  }, [api]);

  const savingAny = savingKeys.size > 0;

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load settings</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }
  if (!principal) return <Center py={80}><Loader color="sage" /></Center>;
  const organicSyncMode = String(
    principal.organicSyncMode
    || (principal.autoIngestOrganicConnects === true ? 'import-new' : 'match-only')
  );
  const organicSyncSince = String(principal.organicSyncSince || '').trim() || null;
  const organicSyncSinceLabel = organicSyncSince
    ? new Date(organicSyncSince).toLocaleDateString()
    : null;
  const twilioConfigured = principal?.twilio?.configured === true;
  const twilioStatusSidLast4 = String(principal?.twilio?.accountSidLast4 || '').trim();
  const twilioStatusFromNumber = String(principal?.twilio?.fromNumber || '').trim();

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 780, margin: '0 auto' }}>
      <Stack gap="xl">
        <Group justify="space-between" align="baseline">
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 34, fontFamily: 'inherit' }}>
            Settings
          </Text>
          {savingAny && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>saving…</Text>
          )}
        </Group>

        {!principal.consentAcceptedAt && (
          <Stack gap="sm">
            <SectionLabel>Consent</SectionLabel>
            <Paper
              radius="lg"
              p="lg"
              style={{ background: 'transparent', border: '1px dashed var(--mantine-color-sage-4)' }}
            >
              <Stack gap="sm">
                <Text size="sm" style={{ color: 'var(--aviary-ink)', whiteSpace: 'pre-wrap' }}>
                  {CONSENT_COPY}
                </Text>
                <Group justify="flex-end">
                  <Button color="sage" onClick={doAcceptConsent} loading={consentSaving}>
                    I understand — enable auto-send
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        )}

        {/* v3.3.1: Sending Rules + Send Modes sections removed — Send-mode
            ladder is Phase-3-deferred per D11, and the Sending Rules toggles
            (businessHoursOnly/dailySendCap/followUpsCount/approvalRequired)
            are legacy for those deferred modes. Underlying principal fields
            retained on the model so backend paths that read them still work
            with their current values. */}

        {/* ─── AVIARY ASSISTANT ───────────────────────────────────── */}
        <Stack gap="sm">
          <SectionLabel>Aviary Assistant</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="sm">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Stack gap={2}>
                  <Text size="sm" fw={700} style={{ color: 'var(--aviary-ink)' }}>
                    Aviary Assistant {extensionRelease?.version ? `· v${extensionRelease.version}` : ''}
                  </Text>
                  <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                    Chrome only
                  </Text>
                </Stack>
              </Group>

              {extensionRelease?.downloadUrl ? (
                <Button
                  component="a"
                  href={String(extensionRelease.downloadUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="sage"
                  size="md"
                  fullWidth
                  leftSection={<IconDownload size={16} />}
                >
                  Download extension
                </Button>
              ) : (
                <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                  {extensionReleaseError || 'Not yet released — check back soon'}
                </Text>
              )}

              <UnstyledButton
                onClick={() => setExtensionInstallOpen((prev) => !prev)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'fit-content' }}
              >
                {extensionInstallOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                  How to install
                </Text>
              </UnstyledButton>

              {extensionInstallOpen && (
                <Box
                  style={{
                    border: '1px solid var(--aviary-disabled)',
                    borderRadius: 10,
                    padding: '10px 14px',
                    background: '#FCFCFC',
                  }}
                >
                  <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--aviary-muted)', fontSize: 13, lineHeight: 1.7 }}>
                    <li>Extract the downloaded .zip</li>
                    <li>Open <code>chrome://extensions</code> in Chrome</li>
                    <li>Toggle Developer mode on (top-right)</li>
                    <li>Click Load unpacked</li>
                    <li>Select the extracted folder</li>
                  </ol>
                </Box>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* ─── LINKEDIN DETECTION ──────────────────────────────────── */}
        <Stack gap="sm" id="linkedin-detection-section">
          <SectionLabel>Detect connection responses</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="sm">
              <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                Detect LinkedIn responses
              </Text>
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                Connect your LinkedIn so Aviary can see when someone accepts your invite. We only listen - we never send on your behalf until you enable Auto-send.
              </Text>
              {!linkedLinkedinAccount ? (
                <Group justify="flex-start">
                  <Button
                    color="sage"
                    leftSection={<IconBrandLinkedin size={16} />}
                    onClick={connectLinkedin}
                    loading={linkingLinkedin}
                  >
                    Connect LinkedIn
                  </Button>
                </Group>
              ) : (
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Stack gap={2}>
                    <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                      Connected
                    </Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                      {String(
                        linkedLinkedinAccount.accountEmail
                        || linkedLinkedinAccount.email
                        || linkedLinkedinAccount.accountId
                        || 'LinkedIn account'
                      )}
                    </Text>
                  </Stack>
                  <Button
                    variant="default"
                    color="gray"
                    onClick={disconnectLinkedin}
                    loading={unlinkingLinkedin}
                  >
                    Unlink
                  </Button>
                </Group>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Stack gap="sm">
          <SectionLabel>Send method</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="xs">
              <SegmentedControl
                value={String(principal.sendMethod || 'extension')}
                onChange={handleSendMethodChange}
                data={[
                  { value: 'extension', label: 'Extension' },
                  { value: 'unipile', label: 'Unipile', disabled: !linkedLinkedinAccount },
                  { value: 'sms', label: 'SMS', disabled: !principal?.twilio?.configured },
                ]}
                fullWidth
                color="sage"
              />
              {!linkedLinkedinAccount && (
                <Stack gap={4}>
                  <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                    Link LinkedIn via Unipile first.
                  </Text>
                  <Group>
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => {
                        document
                          .getElementById('linkedin-detection-section')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      Connect LinkedIn
                    </Button>
                  </Group>
                </Stack>
              )}
              {sendMethodError && (
                <Alert color="red" variant="light" radius="md">
                  <Text size="sm">{sendMethodError}</Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Stack gap="sm">
          <SectionLabel>SMS (Twilio)</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="sm">
              {!twilioEditing && twilioConfigured ? (
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Stack gap={2}>
                    <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                      Configured ✓
                    </Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                      {`•••${twilioStatusSidLast4 || '----'} / ${twilioStatusFromNumber || 'Messaging Service only'}`}
                    </Text>
                  </Stack>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => {
                        setTwilioEditing(true);
                        setTwilioDraft({
                          accountSid: '',
                          authToken: '',
                          fromNumber: '',
                          messagingServiceSid: '',
                        });
                        setTwilioFieldError('');
                      }}
                    >
                      Replace
                    </Button>
                    <Button size="xs" color="red" variant="default" onClick={() => setTwilioClearModalOpen(true)}>
                      Clear
                    </Button>
                  </Group>
                </Group>
              ) : (
                <>
                  <TextInput
                    label="Account SID"
                    placeholder="AC..."
                    value={twilioDraft.accountSid}
                    onChange={(e) => patchTwilioDraft('accountSid', e.currentTarget.value)}
                  />
                  <PasswordInput
                    label="Auth Token"
                    placeholder="Paste Twilio auth token"
                    value={twilioDraft.authToken}
                    onChange={(e) => patchTwilioDraft('authToken', e.currentTarget.value)}
                  />
                  <TextInput
                    label="From Number"
                    placeholder="+61412345678"
                    value={twilioDraft.fromNumber}
                    onChange={(e) => patchTwilioDraft('fromNumber', e.currentTarget.value)}
                  />
                  <TextInput
                    label="Messaging Service SID (optional)"
                    placeholder="MG..."
                    value={twilioDraft.messagingServiceSid}
                    onChange={(e) => patchTwilioDraft('messagingServiceSid', e.currentTarget.value)}
                  />
                  {twilioFieldError ? (
                    <Text size="xs" c="red">{twilioFieldError}</Text>
                  ) : null}
                  <Group justify="flex-end">
                    {twilioConfigured ? (
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => setTwilioEditing(false)}
                        disabled={twilioSaving}
                      >
                        Cancel
                      </Button>
                    ) : null}
                    <Button color="sage" onClick={saveTwilioConfig} loading={twilioSaving}>
                      Save
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Stack gap="sm">
          <SectionLabel>Agent access</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="md">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Stack gap={2}>
                  <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                    Pair Hermes agent
                  </Text>
                  <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                    Generates a one-time pairing code. Generating a new code invalidates previous unused codes.
                  </Text>
                </Stack>
                <Button color="sage" onClick={handleGeneratePairingCode} loading={agentPairingLoading}>
                  Generate pairing code
                </Button>
              </Group>

              {agentPairingCode ? (
                <Paper radius="md" withBorder p="md" style={{ background: '#FCFCFC' }}>
                  <Stack gap={8}>
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                        Expires in 15 minutes
                        {agentPairingExpiresAt ? ` (${formatDateTime(agentPairingExpiresAt)})` : ''}
                      </Text>
                      <Button size="xs" variant="default" onClick={handleCopyPairingCode}>Copy code</Button>
                    </Group>
                    <Text
                      ff="monospace"
                      fw={700}
                      size="lg"
                      style={{ letterSpacing: 1, wordBreak: 'break-all', color: 'var(--aviary-ink)' }}
                    >
                      {agentPairingCode}
                    </Text>
                  </Stack>
                </Paper>
              ) : null}

              <Stack gap={6}>
                <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                  Agent tokens
                </Text>
                <Table withTableBorder withColumnBorders striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Last used</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {agentTokensLoading ? (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Group justify="center"><Loader size="sm" color="sage" /></Group>
                        </Table.Td>
                      </Table.Tr>
                    ) : null}
                    {!agentTokensLoading && agentTokens.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>No agent tokens yet.</Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : null}
                    {!agentTokensLoading && agentTokens.map((token) => {
                      const tokenId = String(token?.tokenId || '');
                      const isRevoked = Boolean(token?.revokedAt);
                      return (
                        <Table.Tr key={tokenId}>
                          <Table.Td>{String(token?.label || tokenId || '-')}</Table.Td>
                          <Table.Td>{formatDateTime(token?.createdAt)}</Table.Td>
                          <Table.Td>{formatDateTime(token?.lastUsedAt)}</Table.Td>
                          <Table.Td>
                            <Badge color={isRevoked ? 'gray' : 'sage'} variant="light">
                              {isRevoked ? 'Revoked' : 'Active'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              size="xs"
                              variant="default"
                              color="red"
                              disabled={isRevoked}
                              onClick={() => {
                                setTokenToRevoke(token);
                                setRevokeModalOpen(true);
                              }}
                            >
                              Revoke
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Stack>
          </Paper>
        </Stack>

        <Stack gap="sm">
          <SectionLabel>Website</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="sm">
              <TextInput
                label="Website URL"
                placeholder="https://altum.gallery"
                value={String(principal.websiteUrl || '')}
                onChange={(e) => patchWebsiteUrl(e.currentTarget.value)}
              />
              {websiteError && (
                <Text size="xs" c="red">{websiteError}</Text>
              )}

              <Textarea
                label="Include patterns"
                placeholder={'/blog/*\n/products/**'}
                autosize
                minRows={2}
                value={(Array.isArray(principal.crawlIncludePatterns) ? principal.crawlIncludePatterns : []).join('\n')}
                onChange={(e) => patchPatternField('crawlIncludePatterns', e.currentTarget.value)}
              />

              <Textarea
                label="Exclude patterns"
                placeholder={'/author/\n/tag/'}
                autosize
                minRows={2}
                value={(Array.isArray(principal.crawlExcludePatterns) ? principal.crawlExcludePatterns : []).join('\n')}
                onChange={(e) => patchPatternField('crawlExcludePatterns', e.currentTarget.value)}
              />

              <NumberInput
                label="Max pages per crawl"
                min={1}
                max={500}
                value={Number(principal.crawlMaxPages || 100)}
                onChange={patchCrawlMaxPages}
              />

              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                Crawler reads server-rendered HTML only (no JavaScript rendering).
              </Text>

              <Group justify="flex-end">
                <Button
                  variant="default"
                  onClick={triggerWebsiteCrawl}
                  loading={crawlNowRunning}
                  disabled={!String(principal.websiteUrl || '').trim() || Boolean(websiteError)}
                >
                  Crawl now
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stack>

        {/* ─── LINKEDIN AUTO INVITES ──────────────────────────────── */}
        <Stack gap="sm">
          <SectionLabel>LinkedIn auto-invite</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="sm">
              <Stack gap={6}>
                <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>Organic sync</Text>
                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                  Choose whether to only match Aviary-invited leads, or also import brand-new LinkedIn connections.
                </Text>
                <Radio.Group
                  value={organicSyncMode}
                  onChange={handleOrganicSyncModeChange}
                >
                  <Stack gap={6}>
                    <Radio
                      value="match-only"
                      label="Match only - promote invited -> connected for people you've reached out to via Aviary."
                      color="sage"
                    />
                    <Radio
                      value="import-new"
                      label={`Import all new connections since ${organicSyncSinceLabel || '...'} - plus ingest any brand-new connection since your earliest active campaign started.`}
                      color="sage"
                      disabled={!organicSyncSince}
                    />
                    {!organicSyncSince && (
                      <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                        Create and activate a campaign to enable this.
                      </Text>
                    )}
                  </Stack>
                </Radio.Group>
              </Stack>

              {organicSyncError && (
                <Alert color="red" variant="light" radius="md">
                  <Text size="sm">{organicSyncError}</Text>
                </Alert>
              )}

              <OrganicSyncBanner
                syncState={principal.organicSyncState}
                onRunNow={triggerBackfillNow}
                running={organicSyncRunning}
              />

              {!prospectAutoStatus?.extensionConnected && (
                <Paper radius="md" p="sm" style={{ background: '#FFF5F5', border: '1px solid #FFD5D5' }}>
                  <Stack gap={6}>
                    <Text size="sm" style={{ color: '#9C2A2A' }}>
                      Chrome extension not detected. Install & sign in from the extension popup to enable Semi mode.
                    </Text>
                    <Group>
                      <Button
                        component="a"
                        href="/docs/extension"
                        variant="light"
                        color="red"
                        size="xs"
                      >
                        Download extension
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              )}

              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                variant="light"
                radius="md"
              >
                <Text size="sm">
                  Account safety: auto-send is currently disabled. Build up your daily invite cadence above 5 per day before turning this on.
                </Text>
              </Alert>

              <Row
                label="Timezone"
                sub="Business-hours gate uses this timezone (Mon-Fri, 9am-5pm)."
                control={(
                  <Select
                    value={String(principal.timezone || DEFAULTS.timezone)}
                    onChange={(value) => { if (value) saveField('timezone', value); }}
                    data={TIMEZONE_OPTIONS}
                    searchable
                    styles={{ input: { textAlign: 'right', width: 260 } }}
                    comboboxProps={{ withinPortal: true }}
                  />
                )}
              />

              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                Prospect lane automation + throttle now live on the Board — open the ⚙ on the Prospect lane header.
              </Text>

              {(() => {
                // v3.3.1: broadened trigger — the extension gate at
                // prospectInviteQueue.js:250 trips on ANY of circuitBrokenAt /
                // consecutiveFailures>=3 / pausedUntil>now. Prior UI only
                // checked circuitBrokenAt so the button never appeared even
                // when the coach was actually gated (Kalani 2026-09-17).
                const now = Date.now();
                const isTripped = (state) => {
                  const s = state || {};
                  if (s.circuitBrokenAt) return true;
                  if (Number(s.consecutiveFailures || 0) >= 3) return true;
                  const pausedUntilTs = Date.parse(s.pausedUntil || '');
                  if (!Number.isNaN(pausedUntilTs) && pausedUntilTs > now) return true;
                  return false;
                };
                const inviteTripped = isTripped(principal.inviteAutoState);
                const messageTripped = isTripped(principal.messageAutoState);
                if (!inviteTripped && !messageTripped) return null;

                const cleared = {
                  paused: false,
                  pausedReason: null,
                  pausedUntil: null,
                  circuitBrokenAt: null,
                  consecutiveFailures: 0,
                };
                const reason = inviteTripped && messageTripped
                  ? 'Prospect + Connected lanes'
                  : inviteTripped ? 'Prospect lane' : 'Connected lane';

                return (
                  <Group justify="space-between" align="center">
                    <Text size="sm" c="yellow.8">
                      Extension paused ({reason}) — resume to keep sending.
                    </Text>
                    <Button
                      variant="default"
                      onClick={async () => {
                        // Reset both lanes so the coach doesn't have to think
                        // about which specifically tripped.
                        await updateMyPrincipal(api, {
                          inviteAutoState: cleared,
                          messageAutoState: cleared,
                        });
                        await loadPrincipal();
                        await refreshStatus();
                        notifications.show({
                          title: 'Extension resumed',
                          message: 'Sends will resume on the next poll (~30s).',
                          color: 'sage',
                        });
                      }}
                    >
                      Resume extension
                    </Button>
                  </Group>
                );
              })()}
            </Stack>
          </Paper>
        </Stack>

        {/* ─── CALENDAR ─────────────────────────────────────────────── */}
        <Stack gap="sm">
          <SectionLabel>Calendar</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap={4}>
              <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>Booking link</Text>
              <TextInput
                value={String(principal.bookingUrl || '')}
                onChange={(e) => patchField('bookingUrl', e.currentTarget.value)}
                placeholder="cal.com/your-handle/intro"
                variant="unstyled"
                styles={{ input: { fontSize: 13, color: 'var(--aviary-muted)', padding: 0, minHeight: 24 } }}
              />
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                used in every message
              </Text>
            </Stack>
          </Paper>
        </Stack>

        {/* ─── ARTICLE BYLINE ───────────────────────────────────────── */}
        <Stack gap="sm">
          <SectionLabel>Article byline</SectionLabel>
          <Paper radius="lg" withBorder p="lg" style={{ background: '#fff' }}>
            <Stack gap="md">
              <Group align="center" justify="space-between" wrap="nowrap">
                <Group gap="md">
                  <Avatar
                    src={bylineDraft.photoUrl || null}
                    size={88}
                    radius={88}
                    color="sage"
                  >
                    {(bylineDraft.displayName || principal.name || 'C').slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Stack gap={2}>
                    <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                      Photo
                    </Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                      JPEG or PNG, up to 5MB.
                    </Text>
                  </Stack>
                </Group>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: 'none' }}
                  onChange={onPhotoPicked}
                />
                <Button
                  variant="light"
                  color="sage"
                  leftSection={<IconCamera size={16} />}
                  onClick={() => photoInputRef.current?.click()}
                  loading={photoUploading}
                >
                  Upload photo
                </Button>
              </Group>

              <TextInput
                label="Display name"
                value={bylineDraft.displayName}
                onChange={(e) => patchBylineDraft('displayName', e.currentTarget.value)}
              />
              <Textarea
                label="Short bio"
                placeholder="1-2 sentences about you"
                autosize
                minRows={2}
                value={bylineDraft.shortBio}
                onChange={(e) => patchBylineDraft('shortBio', e.currentTarget.value)}
              />
              <TextInput
                label="Credential"
                placeholder="ICF-PCC - 15 years coaching engineering leaders"
                value={bylineDraft.credentialLine}
                onChange={(e) => patchBylineDraft('credentialLine', e.currentTarget.value)}
              />
              <TextInput
                label="CTA label"
                placeholder="Book a 1:1 session"
                value={bylineDraft.ctaLabel}
                onChange={(e) => patchBylineDraft('ctaLabel', e.currentTarget.value)}
              />
              <TextInput
                label="CTA URL"
                placeholder="https://calendly.com/..."
                value={bylineDraft.ctaUrl}
                onChange={(e) => patchBylineDraft('ctaUrl', e.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button color="sage" onClick={saveByline} loading={bylineSaving}>
                  Save
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stack>

        {/* ─── LEGACY ───────────────────────────────────────────────── */}
        <Stack gap="sm">
          <SectionLabel>Legacy</SectionLabel>
          <Paper
            radius="lg"
            p="lg"
            style={{
              background: '#FFF7E6',
              border: '2px dashed #C79646',
            }}
          >
            <Group justify="space-between" align="center" wrap="nowrap">
              <Group gap="md" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                <Box
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: '1.5px solid #C79646',
                    color: '#8A5A00',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                  }}
                >
                  v1.0
                </Box>
                <Stack gap={2}>
                  <Text size="sm" fw={700} style={{ color: '#8A5A00' }}>The Aviary 1.0</Text>
                  <Text size="xs" style={{ color: '#8A5A00', opacity: 0.85 }}>
                    the old version, still running — temporary while 2.0 settles in
                  </Text>
                </Stack>
              </Group>
              <UnstyledButton
                component="a"
                href="/pipeline"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  border: '1.5px solid #C79646',
                  borderRadius: 8,
                  color: '#8A5A00',
                  fontSize: 13,
                  fontWeight: 600,
                  background: 'transparent',
                }}
              >
                Open 1.0 <IconExternalLink size={14} />
              </UnstyledButton>
            </Group>
          </Paper>
        </Stack>
      </Stack>

      <Modal
        opened={twilioClearModalOpen}
        onClose={() => {
          if (twilioClearing) return;
          setTwilioClearModalOpen(false);
        }}
        title="Clear Twilio configuration"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Remove SMS (Twilio) configuration from this principal?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setTwilioClearModalOpen(false)} disabled={twilioClearing}>
              Cancel
            </Button>
            <Button color="red" onClick={clearTwilioConfig} loading={twilioClearing}>
              Clear
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={revokeModalOpen}
        onClose={() => {
          if (tokenRevoking) return;
          setRevokeModalOpen(false);
          setTokenToRevoke(null);
        }}
        title="Revoke agent token"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Revoke token
            {' '}
            <Text component="span" fw={700}>{String(tokenToRevoke?.label || tokenToRevoke?.tokenId || '')}</Text>
            ?
          </Text>
          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
            Any Hermes box using this token will lose access immediately.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setRevokeModalOpen(false);
                setTokenToRevoke(null);
              }}
              disabled={tokenRevoking}
            >
              Cancel
            </Button>
            <Button color="red" onClick={confirmRevokeToken} loading={tokenRevoking}>
              Revoke token
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={autoRiskModalOpen}
        onClose={() => setAutoRiskModalOpen(false)}
        title="Enable auto-send"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {`Aviary will send LinkedIn invites on your behalf, up to ${Number(principal.dailyInviteCap || 20)}/day during business hours (${String(principal.timezone || 'Australia/Sydney')}, Mon-Fri) with natural pauses between sends.

A tip: build a daily cadence of 5+ manual invites first so LinkedIn sees the pattern as normal use before you turn this on. You can turn auto off any time.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAutoRiskModalOpen(false)}>Cancel</Button>
            <Button color="sage" loading={enablingAuto} onClick={enableProspectAutoWithConsent}>
              Enable auto
            </Button>
          </Group>
        </Stack>
      </Modal>

    </Box>
  );
}
